import { useState, useEffect } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { SearchInput, SkeletonText } from '@/components';
import { V2Table } from '@/common/components';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';
import { useFormatters } from '../hooks/useFormatters';
import { getGmailCampaigns } from '../api';

import { DashboardPage, DashboardFilterBar } from '../components/PremiumDesign';

const encodeId = (id) => (id ? btoa(String(id)) : '');

export function EmailCampaignListPage() {
  const { formatDate } = useFormatters();
  const navigate = useNavigate();

  const getCompanyId = () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userObj = JSON.parse(storedUser);
        return userObj?.company_id || localStorage.getItem('company_id');
      }
    } catch (e) {
      console.error('Failed to parse user object from localStorage', e);
    }
    return localStorage.getItem('company_id');
  };

  const companyId = getCompanyId();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const {
    data: rawCampaignData,
    isLoading: isCampaignsInitialLoading,
    isFetching: isCampaignsFetching,
    isError: isCampaignsError,
    error: campaignsError,
  } = useQuery({
    queryKey: ['email-bulk-campaigns', companyId, debouncedSearch, currentPage],
    queryFn: async () => {
      const params = { page: currentPage };
      if (debouncedSearch) params.search = debouncedSearch;
      return getGmailCampaigns(companyId, params);
    },
    enabled: !!companyId,
    placeholderData: keepPreviousData,
  });

  const showDelayedSkeleton = useDelayedSkeleton(
    isCampaignsFetching && rawCampaignData !== undefined,
    300,
    500,
  );
  const isCampaignsLoadingSkeleton =
    isCampaignsInitialLoading || showDelayedSkeleton;

  useEffect(() => {
    if (isCampaignsError) {
      toast.error(
        campaignsError?.response?.data?.detail ||
          'Failed to load email campaigns',
      );
    }
  }, [isCampaignsError, campaignsError]);

  // Handle both array response and paginated envelope response
  const campaignsList = Array.isArray(rawCampaignData)
    ? rawCampaignData
    : rawCampaignData?.results || [];

  const totalCount = Array.isArray(rawCampaignData)
    ? rawCampaignData.length
    : rawCampaignData?.count || 0;

  const formatCampDate = (dateString) => {
    if (!dateString) return '—';
    return formatDate(dateString, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const emailCampaignColumns = [
    {
      id: 'name',
      label: 'Campaign Name',
      defaultWidth: 260,
      minWidth: 160,
      resizable: true,
      skeleton: <SkeletonText width="w-36" height="h-4" />,
      cell: (camp) => (
        <span
          className="truncate block text-[13px] sm:text-sm font-medium text-[#0F172A] whitespace-nowrap"
          title={camp.campaign_name || ''}
        >
          {camp.campaign_name || '—'}
        </span>
      ),
    },
    {
      id: 'total',
      label: 'Total Recipients',
      defaultWidth: 160,
      minWidth: 120,
      resizable: true,
      align: 'center',
      skeleton: <SkeletonText width="w-8" height="h-4" className="mx-auto" />,
      cell: (camp) => (
        <span className="truncate block tabular-nums text-[13px] sm:text-sm font-medium text-[#1F2241]">
          {camp.total_count || 0}
        </span>
      ),
    },
    {
      id: 'sent',
      label: 'Sent',
      headerClassName: 'text-emerald-300',
      defaultWidth: 110,
      minWidth: 80,
      resizable: true,
      align: 'center',
      skeleton: <SkeletonText width="w-8" height="h-4" className="mx-auto" />,
      cell: (camp) => (
        <span className="truncate block tabular-nums text-[13px] sm:text-sm font-medium text-[#059669]">
          {camp.sent_count || 0}
        </span>
      ),
    },
    {
      id: 'created',
      label: 'Created Date',
      defaultWidth: 200,
      minWidth: 150,
      resizable: true,
      skeleton: <SkeletonText width="w-28" height="h-4" />,
      cell: (camp) => (
        <span
          className="truncate block text-[13px] sm:text-sm text-text-muted whitespace-nowrap"
          title={formatCampDate(camp.created_at)}
        >
          {formatCampDate(camp.created_at)}
        </span>
      ),
    },
    {
      id: 'action',
      label: 'Action',
      defaultWidth: 140,
      minWidth: 120,
      resizable: false,
      align: 'center',
      stopPropagation: true,
      skeleton: (
        <SkeletonText
          width="w-[100px]"
          height="h-7"
          className="mx-auto rounded-lg"
        />
      ),
      cell: (camp) => (
        <button
          type="button"
          onClick={() => navigate(`/email-campaigns/${encodeId(camp.id)}`)}
          className="h-7 px-2.5 rounded-lg text-xs font-semibold text-primary hover:bg-primary/20 bg-primary/10 border border-primary/20 inline-flex items-center justify-center transition-all focus:outline-none whitespace-nowrap"
        >
          View Campaign
        </button>
      ),
    },
  ];

  return (
    <DashboardPage className="h-full flex-1 min-h-0 flex flex-col overflow-hidden !pt-2 sm:!pt-4">
      <div className="flex flex-col text-left w-full gap-3 sm:gap-4 flex-1 min-h-0">
        {/* Filter Bar */}
        <div className="shrink-0">
          <DashboardFilterBar className="!mb-0">
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto flex-grow max-w-lg">
              <SearchInput
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search by Campaign Name"
                className="flex-grow"
              />
            </div>
          </DashboardFilterBar>
        </div>

        {/* Table View */}
        <div className="w-full min-w-0 flex-1 min-h-0 flex flex-col campaigns-table-compact [&_tbody_tr]:!h-[46px] [&_thead_tr]:!h-[42px] [&_td]:!py-2 [&_td]:!px-3 [&_th]:!py-2 [&_th]:!px-3">
          <V2Table
            tight
            columns={emailCampaignColumns}
            data={campaignsList}
            rowKey="id"
            storageKey="email_campaigns_table_column_widths"
            loading={isCampaignsLoadingSkeleton}
            loadingRowCount={10}
            onRowClick={(camp) =>
              navigate(`/email-campaigns/${encodeId(camp.id)}`)
            }
            empty={
              <div className="flex flex-col items-center justify-center gap-2">
                <span className="text-3xl">📧</span>
                <p className="text-sm font-semibold text-gray-600">
                  No Email Campaigns found
                </p>
                <p className="text-xs text-gray-400">
                  Create a campaign from Email Templates to get started.
                </p>
              </div>
            }
            pagination={{
              currentPage,
              totalCount,
              itemsPerPage: 10,
              onChange: setCurrentPage,
            }}
          />
        </div>
      </div>
    </DashboardPage>
  );
}
